(() => {
  'use strict';
  if (window.cuplabFormControls) return;
  window.cuplabFormControls = true;
  const controls = new WeakMap();
  let current = null;
  let sequence = 0;
  let pending = false;
  let restoreFocus = null;

  function close() {
    if (!current) return;
    current.menu.hidden = true;
    current.trigger.setAttribute('aria-expanded', 'false');
    current.trigger.removeAttribute('aria-activedescendant');
    current = null;
  }

  function refresh(control) {
    const { select, trigger, value } = control;
    value.textContent = select.selectedOptions[0]?.textContent.trim() || '';
    trigger.disabled = select.matches(':disabled');
    trigger.setAttribute('aria-required', String(select.required));
    trigger.setAttribute('aria-invalid', select.getAttribute('aria-invalid') || 'false');
    if (current === control) {
      if (!select.isConnected || trigger.disabled) close();
      else buildOptions(control);
    }
  }

  function setActive(control, index) {
    const options = [...control.menu.children];
    options.forEach((option, position) => option.classList.toggle('is-active', position === index));
    control.active = index;
    const option = options[index];
    if (option) {
      control.trigger.setAttribute('aria-activedescendant', option.id);
      const top = option.offsetTop;
      const bottom = top + option.offsetHeight;
      if (top < control.menu.scrollTop) control.menu.scrollTop = top;
      if (bottom > control.menu.scrollTop + control.menu.clientHeight) {
        control.menu.scrollTop = bottom - control.menu.clientHeight;
      }
    } else control.trigger.removeAttribute('aria-activedescendant');
  }

  function buildOptions(control) {
    const previous = control.active;
    control.menu.replaceChildren(...[...control.select.options].map((option, index) => {
      const item = document.createElement('div');
      item.className = 'cuplab-select__option';
      item.id = `${control.menu.id}-${index}`;
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', String(option.selected));
      item.setAttribute('aria-disabled', String(option.disabled || option.parentElement.disabled === true));
      item.hidden = option.hidden;
      item.dataset.index = index;
      item.textContent = option.textContent.trim();
      return item;
    }));
    setActive(control, previous ?? control.select.selectedIndex);
  }

  function position(control) {
    const rect = control.trigger.getBoundingClientRect();
    const viewport = window.visualViewport;
    const left = viewport?.offsetLeft || 0;
    const top = viewport?.offsetTop || 0;
    const width = viewport?.width || window.innerWidth;
    const height = viewport?.height || window.innerHeight;
    const below = top + height - rect.bottom - 14;
    const available = Math.max(44, Math.min(320, below));
    control.menu.style.width = `${Math.min(rect.width, width - 20)}px`;
    control.menu.style.left = `${Math.max(left + 10, Math.min(rect.left, left + width - rect.width - 10))}px`;
    control.menu.style.maxHeight = `${available}px`;
    control.menu.style.top = `${rect.bottom + 6}px`;
  }

  function open(control) {
    if (control.trigger.disabled) return;
    close();
    current = control;
    control.active = control.select.selectedIndex;
    buildOptions(control);
    control.menu.hidden = false;
    control.trigger.setAttribute('aria-expanded', 'true');
    const viewport = window.visualViewport;
    const rect = control.trigger.getBoundingClientRect();
    const viewportBottom = (viewport?.offsetTop || 0) + (viewport?.height || window.innerHeight);
    const needed = Math.min(320, control.menu.scrollHeight + 2);
    if (viewportBottom - rect.bottom - 14 < needed) {
      control.trigger.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'instant' });
    }
    position(control);
    setActive(control, control.active);
  }

  function choose(control, index) {
    const option = control.select.options[index];
    if (!option || option.disabled || option.hidden || option.parentElement.disabled === true) return;
    const changed = control.select.selectedIndex !== index;
    control.select.selectedIndex = index;
    close();
    refresh(control);
    control.trigger.focus({ preventScroll: true });
    if (changed) {
      if (control.select.closest('variant-selects')) restoreFocus = control;
      control.select.dispatchEvent(new Event('input', { bubbles: true }));
      control.select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function enabledIndexes(control) {
    return [...control.select.options].flatMap((option, index) =>
      option.disabled || option.hidden || option.parentElement.disabled === true ? [] : [index]);
  }

  function keyboard(event, control) {
    const key = event.key;
    const indexes = enabledIndexes(control);
    if (key === 'Escape') {
      if (current === control) {
        event.preventDefault();
        event.stopPropagation();
        close();
      }
      return;
    }
    if (key === 'Tab') { close(); return; }
    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      if (current === control) choose(control, control.active);
      else open(control);
      return;
    }
    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(key)) {
      event.preventDefault();
      if (current !== control) { open(control); return; }
      const position = indexes.indexOf(control.active);
      const next = key === 'Home' ? 0 : key === 'End' ? indexes.length - 1 :
        Math.max(0, Math.min(indexes.length - 1, position + (key === 'ArrowDown' ? 1 : -1)));
      setActive(control, indexes[next]);
      return;
    }
    if (key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      const now = Date.now();
      control.search = now - (control.searchTime || 0) > 700 ? key : (control.search || '') + key;
      control.searchTime = now;
      if (current !== control) open(control);
      const match = indexes.find(index => control.select.options[index].textContent.trim().toLocaleLowerCase()
        .startsWith(control.search.toLocaleLowerCase()));
      if (match !== undefined) setActive(control, match);
    }
  }

  function enhance(select) {
    if (controls.has(select) || select.multiple || select.size > 1 || select.closest('[hidden]')) return;
    if (select.closest('.dropdown-swatch')) return;
    const wrapper = select.parentElement.classList.contains('select') ? select.parentElement : document.createElement('div');
    if (wrapper !== select.parentElement) {
      select.before(wrapper);
      wrapper.append(select);
    }
    wrapper.classList.add('cuplab-select');
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'cuplab-select__trigger';
    trigger.id = `CuplabSelect-${++sequence}`;
    trigger.setAttribute('role', 'combobox');
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    const value = document.createElement('span');
    value.className = 'cuplab-select__value';
    value.id = `${trigger.id}-value`;
    trigger.append(value);
    const labels = [...select.labels];
    labels.forEach((label, index) => { if (!label.id) label.id = `${trigger.id}-label-${index}`; });
    if (labels.length) trigger.setAttribute('aria-labelledby', `${labels.map(label => label.id).join(' ')} ${value.id}`);
    else if (select.hasAttribute('aria-labelledby')) trigger.setAttribute('aria-labelledby', `${select.getAttribute('aria-labelledby')} ${value.id}`);
    else trigger.setAttribute('aria-label', select.getAttribute('aria-label') || select.name || select.id);
    if (select.hasAttribute('aria-describedby')) trigger.setAttribute('aria-describedby', select.getAttribute('aria-describedby'));
    const menu = document.createElement('div');
    menu.className = 'cuplab-select__menu';
    menu.id = `${trigger.id}-menu`;
    menu.hidden = true;
    menu.setAttribute('role', 'listbox');
    menu.setAttribute('aria-labelledby', labels[0]?.id || value.id);
    trigger.setAttribute('aria-controls', menu.id);
    const control = { select, trigger, value, menu };
    controls.set(select, control);
    wrapper.append(trigger);
    document.body.append(menu);
    select.classList.add('cuplab-select__native');
    select.tabIndex = -1;
    select.setAttribute('aria-hidden', 'true');
    trigger.addEventListener('click', () => current === control ? close() : open(control));
    trigger.addEventListener('keydown', event => keyboard(event, control));
    trigger.addEventListener('focus', () => refresh(control));
    select.addEventListener('focus', () => trigger.focus({ preventScroll: true }));
    select.addEventListener('invalid', event => {
      event.preventDefault();
      trigger.setAttribute('aria-invalid', 'true');
      trigger.focus();
    });
    labels.forEach(label => label.addEventListener('click', event => {
      event.preventDefault();
      trigger.focus();
    }));
    menu.addEventListener('pointerdown', event => event.preventDefault());
    menu.addEventListener('click', event => {
      const option = event.target.closest('[role="option"]');
      if (option) choose(control, Number(option.dataset.index));
    });
    menu.addEventListener('pointermove', event => {
      const option = event.target.closest('[role="option"]');
      if (option?.getAttribute('aria-disabled') === 'false') setActive(control, Number(option.dataset.index));
    });
    new MutationObserver(() => refresh(control)).observe(select, {
      subtree: true, childList: true, characterData: true, attributes: true,
      attributeFilter: ['selected', 'disabled', 'label', 'hidden', 'aria-invalid'],
    });
    refresh(control);
  }

  function scan() {
    pending = false;
    if (current && !current.select.isConnected) close();
    document.querySelectorAll('body.thecuplab-lab select').forEach(select => {
      enhance(select);
      const control = controls.get(select);
      if (control && current !== control) refresh(control);
    });
    document.querySelectorAll('.cuplab-select__menu').forEach(menu => {
      if (!document.getElementById(menu.id.replace(/-menu$/, ''))) menu.remove();
    });
    if (restoreFocus && !restoreFocus.select.isConnected) {
      const replacement = document.getElementById(restoreFocus.select.id);
      const control = replacement && controls.get(replacement);
      if (control && document.activeElement === document.body) control.trigger.focus({ preventScroll: true });
      restoreFocus = null;
    }
  }

  function schedule() {
    if (pending) return;
    pending = true;
    queueMicrotask(scan);
  }

  function start() {
    function removeDuplicateTitles() {
      const normalize = text => text.toLocaleLowerCase('de').replace(/[^a-zäöüß]/g, '');
      const aliases = new Map([
        ['agb', 'allgemeinegeschäftsbedingungen'],
        ['widerrufsrechte', 'widerrufsrecht'],
        ['datenschutz', 'datenschutzerklärung'],
        ['widerrufsbelehrung', 'widerrufsrecht'],
        ['versandbedingungen', 'versand'],
      ]);
      document.querySelectorAll('.main-page-title, .shopify-policy__title').forEach(title => {
        const content = title.nextElementSibling;
        if (!content?.matches('.rte, .shopify-policy__body')) return;
        const heading = content.querySelector('h1, h2, h3');
        if (!heading) {
          if (title.matches('.shopify-policy__title')) {
            content.prepend(title);
            title.classList.add('cuplab-policy-heading');
          }
          return;
        }
        const outer = normalize(title.textContent);
        const inner = normalize(heading.textContent);
        if (outer === inner || aliases.get(outer) === inner || aliases.get(inner) === outer) {
          title.classList.add('cuplab-duplicate-title');
          heading.setAttribute('role', 'heading');
          heading.setAttribute('aria-level', '1');
        } else if (title.matches('.shopify-policy__title')) {
          content.prepend(title);
          title.classList.add('cuplab-policy-heading');
        }
      });
    }
    removeDuplicateTitles();
    document.addEventListener('shopify:section:load', removeDuplicateTitles);
    scan();
    new MutationObserver(records => {
      if (records.some(record => [...record.addedNodes, ...record.removedNodes]
        .some(node => node.nodeType === 1 && (node.matches('select') || node.querySelector('select'))))) schedule();
    }).observe(document.body, { subtree: true, childList: true });
    document.addEventListener('input', schedule);
    document.addEventListener('change', schedule);
    document.addEventListener('reset', () => setTimeout(schedule, 0));
    document.addEventListener('pointerdown', event => {
      if (current && !current.trigger.contains(event.target) && !current.menu.contains(event.target)) close();
    });
    document.addEventListener('focusin', event => {
      if (current && !current.trigger.contains(event.target) && !current.menu.contains(event.target)) close();
    });
    document.addEventListener('scroll', event => {
      if (!current || current.menu.contains(event.target)) return;
      const rect = current.trigger.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) close();
      else position(current);
    }, true);
    window.addEventListener('resize', close, { passive: true });
    window.visualViewport?.addEventListener('resize', close, { passive: true });
    window.visualViewport?.addEventListener('scroll', close, { passive: true });
    document.addEventListener('shopify:section:load', schedule);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
